import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { 
  ClockIcon, 
  EnvelopeIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  UserIcon,
  EyeIcon,
  DocumentTextIcon,
  CameraIcon,
  ShieldExclamationIcon,
  ArrowPathIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import Tooltip from './Tooltip';

interface ActivityLogEntry {
  id: number;
  registration_id: number;
  action: string;
  old_status?: string;
  new_status?: string;
  details?: string;
  performed_by?: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  attendee_name?: string;
  attendee_email?: string;
}

interface ActivityLogProps {
  eventId: number;
}

const ActivityLog: React.FC<ActivityLogProps> = ({ eventId }) => {
  const [logs, setLogs] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 50;

  useEffect(() => {
    loadLogs();
  }, [eventId, filter, page]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      let url = `${process.env.REACT_APP_API_URL}/logs/event/${eventId}/logs?limit=${limit}&offset=${page * limit}`;
      if (filter !== 'all') {
        url += `&action=${filter}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }

      const data = await response.json();
      
      // For each log, fetch the registration details to get attendee info
      const enrichedLogs = await Promise.all(data.map(async (log: ActivityLogEntry) => {
        try {
          const regResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/logs/registration/${log.registration_id}/history`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            }
          );
          
          if (regResponse.ok) {
            const regData = await regResponse.json();
            return {
              ...log,
              attendee_name: regData.attendee_name,
              attendee_email: regData.attendee_email
            };
          }
        } catch (err) {
          // Ignore errors for individual registrations
        }
        return log;
      }));

      if (page === 0) {
        setLogs(enrichedLogs);
      } else {
        setLogs(prev => [...prev, ...enrichedLogs]);
      }
      
      setHasMore(data.length === limit);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'created':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'status_changed':
      case 'updated':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      case 'invitation_sent':
      case 'invitation_resent':
      case 'verification_sent':
      case 'verification_resent':
      case 'approval_sent':
      case 'rejection_sent':
      case 'broadcast_sent':
        return <EnvelopeIcon className="h-5 w-5 text-purple-500" />;
      case 'invitation_opened':
      case 'invitation_accepted':
        return <EyeIcon className="h-5 w-5 text-indigo-500" />;
      case 'invitation_declined':
      case 'deleted':
      case 'rejected':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'email_verified':
      case 'checked_in':
      case 'checked_out':
        return <UserIcon className="h-5 w-5 text-green-500" />;
      case 'badge_generated':
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
      case 'social_card_link_sent':
      case 'social_card_page_visited':
      case 'social_card_downloaded':
        return <CameraIcon className="h-5 w-5 text-pink-500" />;
      case 'scanner_detected':
        return <ShieldExclamationIcon className="h-5 w-5 text-orange-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getActionDescription = (log: ActivityLogEntry) => {
    let description = '';
    
    switch (log.action) {
      case 'created':
        description = 'Registration created';
        break;
      case 'status_changed':
        description = `Status changed from ${log.old_status} to ${log.new_status}`;
        break;
      case 'email_verified':
        description = 'Email address verified';
        break;
      case 'invitation_sent':
        description = 'Invitation email sent';
        break;
      case 'invitation_opened':
        description = 'Invitation opened';
        break;
      case 'invitation_accepted':
        description = 'Invitation accepted';
        break;
      case 'invitation_declined':
        description = 'Invitation declined';
        break;
      case 'checked_in':
        description = 'Checked in at event';
        break;
      case 'checked_out':
        description = 'Checked out from event';
        break;
      case 'verification_sent':
        description = 'Verification email sent';
        break;
      case 'approval_sent':
        description = 'Approval email sent';
        break;
      case 'scanner_detected':
        description = '⚠️ Security scanner detected';
        break;
      case 'social_card_downloaded':
        description = 'Social card downloaded';
        break;
      default:
        description = log.action.replace(/_/g, ' ');
    }
    
    if (log.details) {
      description += ` - ${log.details}`;
    }
    
    return description;
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    
    return format(date, 'MMM d, yyyy HH:mm');
  };

  if (loading && page === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Filter Bar */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Activity Log</h2>
          <div className="flex items-center justify-between gap-2">
            {/* Filter buttons */}
            <Tooltip text="Show all activities">
              <button
                onClick={() => { setFilter('all'); setPage(0); }}
                className={`p-2 rounded-md transition-colors ${
                  filter === 'all' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Show all activities"
              >
                <Squares2X2Icon className="h-5 w-5" />
              </button>
            </Tooltip>
            
            <Tooltip text="Show registrations">
              <button
                onClick={() => { setFilter('created'); setPage(0); }}
                className={`p-2 rounded-md transition-colors ${
                  filter === 'created' 
                    ? 'bg-green-100 text-green-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Show registrations"
              >
                <CheckCircleIcon className="h-5 w-5" />
              </button>
            </Tooltip>
            
            <Tooltip text="Show status changes">
              <button
                onClick={() => { setFilter('status_changed'); setPage(0); }}
                className={`p-2 rounded-md transition-colors ${
                  filter === 'status_changed' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Show status changes"
              >
                <ClockIcon className="h-5 w-5" />
              </button>
            </Tooltip>
            
            <Tooltip text="Show invitations">
              <button
                onClick={() => { setFilter('invitation_sent'); setPage(0); }}
                className={`p-2 rounded-md transition-colors ${
                  filter === 'invitation_sent' 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Show invitations"
              >
                <EnvelopeIcon className="h-5 w-5" />
              </button>
            </Tooltip>
            
            <Tooltip text="Show verifications">
              <button
                onClick={() => { setFilter('email_verified'); setPage(0); }}
                className={`p-2 rounded-md transition-colors ${
                  filter === 'email_verified' 
                    ? 'bg-indigo-100 text-indigo-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Show verifications"
              >
                <EyeIcon className="h-5 w-5" />
              </button>
            </Tooltip>
            
            <Tooltip text="Show check-ins">
              <button
                onClick={() => { setFilter('checked_in'); setPage(0); }}
                className={`p-2 rounded-md transition-colors ${
                  filter === 'checked_in' 
                    ? 'bg-green-100 text-green-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Show check-ins"
              >
                <UserIcon className="h-5 w-5" />
              </button>
            </Tooltip>
            
            <Tooltip text="Show security alerts">
              <button
                onClick={() => { setFilter('scanner_detected'); setPage(0); }}
                className={`p-2 rounded-md transition-colors ${
                  filter === 'scanner_detected' 
                    ? 'bg-orange-100 text-orange-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Show security alerts"
              >
                <ShieldExclamationIcon className="h-5 w-5" />
              </button>
            </Tooltip>
            
            {/* Divider */}
            <div className="w-px h-6 bg-gray-300 mx-1" />
            
            {/* Refresh button */}
            <Tooltip text="Refresh">
              <button
                onClick={() => {
                  setPage(0);
                  loadLogs();
                }}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                title="Refresh"
              >
                <ArrowPathIcon className="h-5 w-5" />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Activity Feed */}
      <div className="divide-y divide-gray-100">
        {logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No activity logs found
          </div>
        ) : (
          logs.map((log) => (
            <div key={log.id} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  {getActionIcon(log.action)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {getActionDescription(log)}
                      </p>
                      {log.attendee_email && (
                        <p className="text-sm text-gray-600 mt-1">
                          {log.attendee_name || 'No name'} - {log.attendee_email}
                        </p>
                      )}
                      {log.performed_by && (
                        <p className="text-xs text-gray-500 mt-1">
                          by {log.performed_by}
                        </p>
                      )}
                      {log.ip_address && log.action === 'scanner_detected' && (
                        <p className="text-xs text-orange-600 mt-1">
                          IP: {log.ip_address}
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0 ml-4">
                      <span className="text-xs text-gray-500">
                        {formatTimeAgo(log.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Load More */}
      {hasMore && logs.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={() => setPage(prev => prev + 1)}
            disabled={loading}
            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityLog;